// src/components/FinanceSystemsExplorer.js
import React, { useState } from "react";
import "./FinanceSystemsExplorer.css";

const FINANCE_SYSTEMS = [
  {
    id: "western",
    name: "Western Finance",
    icon: "🏦",
    tagline: "Debt-driven growth with central banks & markets",
    description: `Western Finance is based on capitalist, free-market principles 
with heavy reliance on debt, credit, and interest-based lending. 
It is dominated by central banks (like the Federal Reserve and ECB), 
stock markets, and derivatives.`,
    principles: [
      "Central banks set interest rates & money supply",
      "Private banks issue loans with interest",
      "Stock markets, bonds, derivatives dominate",
      "Growth fueled by debt & consumption",
    ],
    pros: [
      "High innovation & global capital flows",
      "Dynamic financial markets",
      "Strong international influence",
    ],
    cons: [
      "Prone to bubbles & crashes (2008 crisis)",
      "Rising inequality",
      "Dependence on central banks",
    ],
    examples: "United States, European Union, United Kingdom",
  },
  {
    id: "eastern",
    name: "Eastern Finance",
    icon: "🏯",
    tagline: "State-directed credit & mixed economies",
    description: `Eastern Finance often blends state control with markets. 
Governments direct banks to support strategic industries. 
High savings rates, export-led growth, and state-owned enterprises 
are key features (e.g., China, Japan).`,
    principles: [
      "State influences lending & credit allocation",
      "High savings, lower consumption",
      "Export-driven growth models",
      "Government-owned enterprises",
    ],
    pros: [
      "Stable, long-term planning",
      "Strong infrastructure investment",
      "High savings culture",
    ],
    cons: [
      "Less individual freedom in markets",
      "Risk of inefficiency / corruption",
      "Shadow banking risks",
    ],
    examples: "China, Japan, South Korea",
  },
  {
    id: "islamic",
    name: "Islamic Finance",
    icon: "🕌",
    tagline: "Ethical, interest-free finance system",
    description: `Islamic Finance is based on Shariah law. 
It prohibits interest (riba), speculation (gharar), and investment in haram sectors 
like gambling, alcohol, or weapons. Instead, financing is based on 
profit-loss sharing and asset-backed models.`,
    principles: [
      "No interest (riba) — profit/loss sharing",
      "Ethical restrictions (no haram industries)",
      "Asset-backed financing only",
      "Focus on fairness & stability",
    ],
    pros: [
      "Ethical & socially responsible",
      "Avoids speculative bubbles",
      "Risk shared between lender & borrower",
    ],
    cons: [
      "Limited scalability in global markets",
      "Lower innovation in finance",
      "Still adapting to modern fintech",
    ],
    examples: "Saudi Arabia, UAE, Malaysia, Pakistan",
  },
  {
    id: "crypto",
    name: "Crypto Finance",
    icon: "₿",
    tagline: "Decentralized, digital-first financial system",
    description: `Crypto Finance is based on blockchain technology. 
It is decentralized, trustless, and powered by smart contracts. 
Anyone can transact or build without intermediaries. 
DeFi, NFTs, DAOs, and stablecoins are its key pillars.`,
    principles: [
      "Decentralized networks (no central authority)",
      "Smart contracts automate agreements",
      "Open access to anyone with internet",
      "Volatile but highly innovative",
    ],
    pros: [
      "Open & borderless",
      "Fast innovation",
      "Financial inclusion for the unbanked",
    ],
    cons: [
      "Volatile & risky",
      "Security vulnerabilities (hacks)",
      "Regulatory uncertainty",
    ],
    examples: "Global (Bitcoin, Ethereum, DeFi protocols)",
  },
];

export default function FinanceSystemsExplorer({ onSelect }) {
  const [active, setActive] = useState(null);

  return (
    <div className="finance-explorer">
      <h2>Finance Systems Explorer</h2>
      <p className="intro">
        Learn about the four major financial systems. Click a system to explore
        its principles, pros/cons, and examples.
      </p>

      <div className="finance-grid">
        {FINANCE_SYSTEMS.map((fs) => (
          <div
            key={fs.id}
            className={`finance-card ${active?.id === fs.id ? "active" : ""}`}
            onClick={() => setActive(fs)}
          >
            <span className="icon">{fs.icon}</span>
            <h3>{fs.name}</h3>
            <p>{fs.tagline}</p>
          </div>
        ))}
      </div>

      {active && (
        <div className="finance-details">
          <h3>
            {active.icon} {active.name}
          </h3>
          <p>{active.description}</p>

          <section>
            <h4>Core Principles</h4>
            <ul>
              {active.principles.map((p, i) => (
                <li key={i}>{p}</li>
              ))}
            </ul>
          </section>

          <section>
            <h4>Pros</h4>
            <ul>
              {active.pros.map((p, i) => (
                <li key={i}>✅ {p}</li>
              ))}
            </ul>
          </section>

          <section>
            <h4>Cons</h4>
            <ul>
              {active.cons.map((c, i) => (
                <li key={i}>❌ {c}</li>
              ))}
            </ul>
          </section>

          <p>
            <strong>Examples:</strong> {active.examples}
          </p>

          {onSelect && (
            <button
              className="simulate-btn"
              onClick={() => onSelect(active.id)}
            >
              Simulate This System →
            </button>
          )}
        </div>
      )}

      {/* Modal */}
      {active && (
        <div className="finance-modal-overlay">
          <div className="finance-modal">
            <h3>
              {active.icon} {active.name}
            </h3>
            <p>{active.description}</p>

            <section>
              <h4>Core Principles</h4>
              <ul>
                {active.principles.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </section>

            <section>
              <h4>Pros</h4>
              <ul>
                {active.pros.map((p, i) => (
                  <li key={i}>✅ {p}</li>
                ))}
              </ul>
            </section>

            <section>
              <h4>Cons</h4>
              <ul>
                {active.cons.map((c, i) => (
                  <li key={i}>❌ {c}</li>
                ))}
              </ul>
            </section>

            <p>
              <strong>Examples:</strong> {active.examples}
            </p>

            <div className="modal-actions">
              {onSelect && (
                <button
                  className="simulate-btn"
                  onClick={() => onSelect(active.id)}
                >
                  🚀 Playground
                </button>
              )}
              <button className="cancel-btn" onClick={() => setActive(null)}>
                ❌ Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
}
